// Store comparison wire contract (ADR-0167). The gerencial dashboard's
// «Comparar tiendas» table: one row per scoped store, one column per
// figure, every cell filled by core-api from the database — the model
// never writes a number into it. Two ways in, one shape out:
//
//   - GET /assets/compare (JSON) and GET /assets/compare.xlsx, fed by
//     the dashboard's deterministic chip and by «Exportar».
//   - POST /ia/chat's optional `table`: the model asks for a table with
//     the chat tool (rows by per-turn handle, columns by key) and
//     core-api fills it with the same builder, echoing the resolved
//     query so «Exportar» re-calls the GET above with it.
//
// Transport-only, like ai.ts: there is no comparison entity in
// core-engine to derive from, so per §0.4(7) this schema is the source
// of truth by necessity. Column KEYS are engine-generic on purpose (the
// chat tool schema in ai-svc reuses them, and must not carry one
// vertical's vocabulary); the visible LABELS come from the recipe and
// the data (section labels) at request time.
import { z } from "zod";
// One response never carries more rows than this; scopeTotal says how
// many the caller's scope holds, so the client can say «n de m».
export const STORE_COMPARISON_ROWS_MAX = 500;
// Fixed columns (11) plus one per checklist section the scope carries.
export const STORE_COMPARISON_COLUMNS_MAX = 80;
export const STORE_COMPARISON_FIXED_KEYS = [
    "grupo",
    "activo",
    "marca",
    "ultimo_reporte",
    "score",
    "tendencia",
    "mant_vencidas",
    "mant_por_vencer",
    "mant_sin_registro",
    "mant_al_dia",
    "mant_proximo_vencimiento",
];
// Dynamic per-section columns: `seccion:S3`, the same stable section code
// the chat's facts legend prints (lib/ia-chat-facts.ts assignSectionCodes),
// so a code the model read is the key it asks for.
export const STORE_COMPARISON_SECTION_KEY_RE = /^seccion:S\d{1,3}$/;
export const storeComparisonFixedKey = z.enum(STORE_COMPARISON_FIXED_KEYS);
export const storeComparisonColumnKey = z.union([
    storeComparisonFixedKey,
    z.string().regex(STORE_COMPARISON_SECTION_KEY_RE),
]);
export const storeComparisonSortDir = z.enum(["asc", "desc"]);
export const storeComparisonQuery = z
    .object({
    // A filter INSIDE the caller's scope, never the source of it: core-api
    // intersects these with loadScopedAssets and drops the rest silently.
    assetIds: z.array(z.string().uuid()).min(1).max(STORE_COMPARISON_ROWS_MAX).optional(),
    // Absent = every column the scope has data for. `activo` is always
    // shown, whether asked for or not.
    columns: z.array(storeComparisonColumnKey).min(1).max(STORE_COMPARISON_COLUMNS_MAX).optional(),
    sortBy: storeComparisonColumnKey.optional(),
    sortDir: storeComparisonSortDir.optional(),
    limit: z.number().int().min(1).max(STORE_COMPARISON_ROWS_MAX).optional(),
})
    .strict();
// The GET routes carry the same query as a querystring: lists comma-
// joined (`assetIds=a,b`, `columns=score,seccion:S1`), limit as text.
const commaList = (v) => typeof v === "string" ? v.split(",").filter((s) => s.length > 0) : v;
export const storeComparisonSearch = z
    .object({
    assetIds: z.preprocess(commaList, storeComparisonQuery.shape.assetIds),
    columns: z.preprocess(commaList, storeComparisonQuery.shape.columns),
    sortBy: storeComparisonQuery.shape.sortBy,
    sortDir: storeComparisonQuery.shape.sortDir,
    limit: z.coerce.number().int().min(1).max(STORE_COMPARISON_ROWS_MAX).optional(),
})
    .strict();
/** The querystring storeComparisonSearch parses back into the same query. */
export function storeComparisonSearchParams(query) {
    const params = new URLSearchParams();
    if (query.assetIds)
        params.set("assetIds", query.assetIds.join(","));
    if (query.columns)
        params.set("columns", query.columns.join(","));
    if (query.sortBy)
        params.set("sortBy", query.sortBy);
    if (query.sortDir)
        params.set("sortDir", query.sortDir);
    if (query.limit !== undefined)
        params.set("limit", String(query.limit));
    return params;
}
// text: free label · number: plain figure (score, delta, count) ·
// percent: 0–100 · date: civil date YYYY-MM-DD (America/Santiago) ·
// store: the row's store name, linked by the row's assetId.
export const storeComparisonColumnKind = z.enum(["text", "number", "percent", "date", "store"]);
export const storeComparisonColumnGroup = z.enum(["estado", "mantenciones"]);
export const storeComparisonColumn = z
    .object({
    key: storeComparisonColumnKey,
    label: z.string().min(1).max(200),
    kind: storeComparisonColumnKind,
    group: storeComparisonColumnGroup.optional(),
})
    .strict();
// null = no data for that store (no closed report, section not
// evaluated, no maintenance items) — never 0.
export const storeComparisonCell = z.union([z.string().max(500), z.number().finite(), z.null()]);
export const storeComparisonRow = z
    .object({
    assetId: z.string().uuid(),
    cells: z.record(storeComparisonColumnKey, storeComparisonCell),
})
    .strict();
export const storeComparisonResponse = z
    .object({
    // The instant the figures were read — the table and every export of
    // it print this, not the download time.
    asOf: z.string().datetime(),
    // Stores in the caller's scope; rows.length < scopeTotal means a
    // filtered or capped view.
    scopeTotal: z.number().int().min(0),
    columns: z.array(storeComparisonColumn).max(STORE_COMPARISON_COLUMNS_MAX),
    rows: z.array(storeComparisonRow).max(STORE_COMPARISON_ROWS_MAX),
})
    .strict();
// The chat's table: the filled response plus the resolved query, so
// «Exportar» asks GET /assets/compare.xlsx for exactly these rows.
export const storeComparisonTable = storeComparisonResponse
    .extend({ query: storeComparisonQuery })
    .strict();
//# sourceMappingURL=store-comparison.js.map